import { useState } from 'react'
import {
  Avatar, Box, Button, Chip, CircularProgress, Pagination,
  Stack, Tab, Tabs, Tooltip, Typography,
} from '@mui/material'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { useQuery } from '@tanstack/react-query'
import type { AuditLogEntry } from '@cmms/api-client'

import { api } from '../../lib/api'
import { downloadPdf } from '../../lib/printHtml'
import { toast } from '../../store/snackbarStore'
import { useOrgSettings } from '../../hooks/useOrgSettings'

const MODULES = [
  { key: 'all', label: 'All' },
  { key: 'work_orders', label: 'Work Orders' },
  { key: 'assets', label: 'Assets' },
  { key: 'locations', label: 'Locations' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'checklists', label: 'Checklists' },
  { key: 'vendors', label: 'Vendors' },
  { key: 'sites', label: 'Sites' },
  { key: 'teams', label: 'Teams' },
  { key: 'users', label: 'Users' },
  { key: 'roles', label: 'Roles' },
] as const

const ACTION_COLORS: Record<string, string> = {
  created: '#059669', updated: '#3B82F6', deleted: '#DC2626',
  started: '#3B82F6', executed: '#3B82F6', closed: '#8B5CF6', resolved: '#059669',
  reopened: '#D97706', assigned: '#0E7490', acknowledged: '#059669',
  used_in_work_order: '#D97706', returned_from_work_order: '#0D9488',
  restocked: '#059669', adjusted: '#D97706', reorder_sent: '#EA580C',
  published: '#059669', drafted: '#94A3B8', archived: '#DC2626',
  part_added: '#0D9488', part_removed: '#EA580C',
  comment_added: '#64748B', comment_deleted: '#DC2626',
  members_added: '#059669', member_removed: '#DC2626',
  deactivate: '#DC2626', reactivate: '#059669', role_change: '#8B5CF6',
}

const MODULE_COLORS: Record<string, string> = {
  work_order: '#3B82F6', asset: '#8B5CF6', location: '#0D9488',
  part: '#D97706', checklist: '#EA580C', vendor: '#059669',
  site: '#0E7490', team: '#7C3AED', user: '#DB2777', role: '#64748B',
}

const PAGE_SIZE = 20

export function AuditTrailTab() {
  const { formatDate } = useOrgSettings()
  const [module, setModule] = useState('all')
  const [page, setPage] = useState(1)

  const logsQ = useQuery({
    queryKey: ['audit-logs', module, page],
    queryFn: () => api.analytics.auditLogs({ module, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }),
    staleTime: 30_000,
  })

  const logs = logsQ.data?.logs ?? []
  const total = logsQ.data?.total ?? 0
  const totalPages = Math.ceil(total / PAGE_SIZE)

  async function handleExportPdf() {
    try {
      const html = await api.analytics.auditReportHtml(module)
      await downloadPdf(html, `audit-trail-${module}.pdf`)
    } catch { toast.error('Failed to generate report') }
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Audit trail</Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }}>
            Track all changes across your workspace
          </Typography>
        </Box>
        <Button variant="outlined" size="small" startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 15 }} />}
          onClick={handleExportPdf}
          sx={{ fontSize: 12.5, fontWeight: 600, borderRadius: 2, textTransform: 'none' }}>
          Export PDF
        </Button>
      </Stack>

      <Tabs value={module} onChange={(_, v) => { setModule(v); setPage(1) }} variant="scrollable" scrollButtons="auto"
        sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, px: 2, fontSize: 12.5, fontWeight: 600, textTransform: 'none' } }}>
        {MODULES.map((m) => <Tab key={m.key} value={m.key} label={m.label} />)}
      </Tabs>

      {logsQ.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
      ) : logs.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>No audit entries yet</Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            {/* Header */}
            <Stack direction="row" sx={{ px: 2, py: 1.25, bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
              <Typography sx={{ flex: 2, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Date</Typography>
              <Typography sx={{ flex: 1.5, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Action</Typography>
              <Typography sx={{ flex: 3, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Resource</Typography>
              <Typography sx={{ flex: 1.5, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>By</Typography>
              <Typography sx={{ flex: 2, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Details</Typography>
            </Stack>

            {/* Rows */}
            {logs.map((l, i) => (
              <AuditRow key={`${l.resource_id}-${l.action}-${i}`} log={l} formatDate={formatDate} />
            ))}
          </Box>

          {totalPages > 1 && (
            <Stack alignItems="center" sx={{ pt: 1 }}>
              <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)}
                size="small" shape="rounded" />
            </Stack>
          )}

          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', textAlign: 'center' }}>
            Showing {Math.min((page - 1) * PAGE_SIZE + 1, total)}–{Math.min(page * PAGE_SIZE, total)} of {total} entries
          </Typography>
        </>
      )}
    </Stack>
  )
}

function AuditRow({ log, formatDate }: { log: AuditLogEntry; formatDate: (d: string) => string }) {
  const actionColor = ACTION_COLORS[log.action] ?? '#64748B'
  const moduleColor = MODULE_COLORS[log.module] ?? '#94A3B8'
  const initials = log.actor_name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)

  const meta = log.metadata ?? {}
  const detailParts: string[] = []
  if (meta.status) detailParts.push(`Status: ${meta.status}`)
  if (meta.priority) detailParts.push(`Priority: ${meta.priority}`)
  if (meta.site) detailParts.push(meta.site)
  if (meta.details) detailParts.push(meta.details)
  if (meta.quantity_change) detailParts.push(`Qty: ${meta.quantity_change > 0 ? '+' : ''}${meta.quantity_change}`)
  if (meta.work_order) detailParts.push(`WO: ${meta.work_order}`)
  const detailText = detailParts.join(' · ')

  return (
    <Stack direction="row" alignItems="center"
      sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider',
        transition: 'background 120ms', '&:hover': { bgcolor: 'action.hover' } }}>
      <Box sx={{ flex: 2 }}>
        <Typography sx={{ fontSize: 12.5, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
          {formatDate(log.created_at)}
        </Typography>
      </Box>
      <Box sx={{ flex: 1.5 }}>
        <Chip label={log.action.replace(/_/g, ' ')} size="small"
          sx={{ height: 22, fontSize: 10.5, fontWeight: 700,
            bgcolor: `${actionColor}15`, color: actionColor,
            '& .MuiChip-label': { px: 1 } }} />
      </Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 3, minWidth: 0 }}>
        <Chip label={log.module.replace(/_/g, ' ')} size="small"
          sx={{ height: 18, fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
            bgcolor: `${moduleColor}15`, color: moduleColor,
            '& .MuiChip-label': { px: 0.75 } }} />
        <Typography sx={{ fontSize: 12.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {log.resource_name}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flex: 1.5 }}>
        <Avatar sx={{ width: 22, height: 22, fontSize: 9, fontWeight: 700, bgcolor: moduleColor }}>
          {initials}
        </Avatar>
        <Typography sx={{ fontSize: 12, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {log.actor_name}
        </Typography>
      </Stack>
      <Box sx={{ flex: 2, minWidth: 0 }}>
        {detailText ? (
          <Tooltip title={detailText} arrow placement="top-start">
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {detailText}
            </Typography>
          </Tooltip>
        ) : (
          <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>—</Typography>
        )}
      </Box>
    </Stack>
  )
}
